<template>
  <div class="chat-input-container">
    <LexonTextAreaComponent
      v-model="query"
      :placeholder="props.placeholder"
      class="chat-textarea"
      @keydown.native.enter.exact.prevent="handleEnterKey"
    />
    <v-btn :class="['send-btn', { 'send-btn--active': query.trim() }]" @click="handleButtonClick" elevation="0" icon>
      <img v-if="!isLoading" :src="sendIcon" alt="Enviar" width="20" height="20" />
      <img v-else :src="stopIcon" alt="Cancelar" width="17" height="17" />
    </v-btn>
    <div class="chat-toolbar">
      <ChatFilterComponent />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import LexonTextAreaComponent from '@/components/forms/fields/LexonTextArea/LexonTextAreaComponent.vue'
import ChatFilterComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIaBoxQuery/components/ChatFilterComponent.vue'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'

const props = defineProps({
  placeholder: {
    type: String,
    default: ''
  },
  modelValue: {
    type: String,
    default: ''
  },
  isLoading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['new-query', 'cancel-query'])

const searchIAStore = useSearchIAStore()
const query = ref(props.modelValue)

watch(
  () => props.modelValue,
  (val) => {
    query.value = val
  }
)

const handleEnterKey = (event: KeyboardEvent) => {
  // Solo enviar si no está cargando
  if (!props.isLoading && query.value.trim()) {
    event.preventDefault()
    sendQuery()
  }
}

const handleButtonClick = async () => {
  if (props.isLoading) {
    emit('cancel-query')
    searchIAStore.resetSearchId()
  } else {
    await searchIAStore.generateSearchId()
    sendQuery()
  }
}

const sendQuery = () => {
  const filter = {
    type: searchIAStore.filterType || '',
    value: searchIAStore.filterValue
  }

  trackEvent(TrackerEvents.AI_SEARCH, {
    query: query.value,
    filter: filter.type !== '',
    filter_property_selected: filter.type === '' ? '' : filter.type,
    search_id: searchIAStore.search_id
  })
  emit('new-query', {
    query: query.value,
    filter: filter
  })
}

const sendIcon = require('@/assets/images/svg/icon-send.svg')
const stopIcon = require('@/assets/images/svg/icon-stop.svg')
</script>

<style lang="scss" scoped>
.chat-input-container {
  display: flex;
  flex-direction: column;
  position: relative;

  ::deep(.lf-textarea-input) {
    border: 1px solid $neutral-grey-400;
    border-radius: 1px;
    height: 130px;
  }
}

.chat-toolbar {
  position: absolute;
  padding: $spacing-xs 0;
  gap: $spacing-sm;
  width: 400px;
  bottom: 20px;
  left: 8px;
}

.chat-textarea {
  flex: 1;
  margin-right: 0;
  :deep(.lf-textarea-input) {
    height: 134px;
    background-color: $neutral-white-000;
    border-radius: 12px !important;
    border: 1px solid $main-500 !important;
    padding: $spacing-sm !important;
    &:hover .lf-textarea {
      background-color: $neutral-white-000 !important;
    }
    .lf-textarea {
      border: none !important;
      padding: 0;
      max-height: 73px !important;
    }
  }
  :deep(.clean-icon) {
    top: 16px;
  }
}

.send-btn {
  min-width: 30px;
  min-height: 30px;
  border-radius: 8px;
  background-color: $neutral-grey-300 !important;
  position: absolute;
  right: 8px;
  bottom: 27px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s;
  vertical-align: middle;

  img {
    filter: invert(47%) sepia(5%) saturate(437%) hue-rotate(184deg) brightness(92%) contrast(88%);
  }

  &--active {
    background-color: $main-1000 !important;

    img {
      filter: brightness(0) invert(100%);
    }
  }
}
</style>
